"use client";

import { useEffect, useState } from "react";

import Image from "next/image";

import { PERSIST_KEY, resetToMain } from "@repo/store";
import { useTokenRefresh } from "@repo/token";
import { getNewToken } from "@repo/token/utils/getNewToken";
import { ChevronRightCircle } from "lucide-react";

import { fetchUser } from "@repo/ui/api/fetchUser";
import JetSmartLogo from "@repo/ui/assets/logos/jetsmart_logo.svg";
import { JetSmartButton } from "@repo/ui/components/common/forms/JetSmartButton";
import { FlightSpinLoader } from "@repo/ui/components/common/Loader/FlightSpinLoader";
import CountrySelector from "@repo/ui/components/Header/CountrySelector";
import DesktopMenuGate from "@repo/ui/components/Header/DesktopMenuGate";
import LoginDialog from "@repo/ui/components/Header/LoginDialog";
import MobileMenu from "@repo/ui/components/Header/MobileMenu";
import { roles } from "@repo/ui/constants/userRoles";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import { useLocalizedRouter, useZonePathname } from "@repo/ui/navigation";
import type { CultureNames } from "@repo/ui/types/cms";
import type { PageFooterHeader } from "@repo/ui/types/cms/common";

import { cleanupReduxForTab } from "@repo/store/cleanup";
import { useAppDispatch } from "@repo/store/hooks";
import { setAuth, useAuthState, type AuthUser } from "@repo/store/slices";

import { CURRENT_ZONE, homeUrl, zoneUrl } from "../../constants/bookingBase";
import RegisterButton from "./RegisterButton";
import UserDropdown from "./UserDropdown";

interface HeaderProps {
  headerContent: PageFooterHeader["header"];
  selectedCountry: PageFooterHeader["header"]["countryDropdown"][number] | null;
  showModal?: () => void;
  cultureNames: CultureNames;
  /** Optional URL for the Signup link (e.g. /signup). */
  signupHref?: string;
}

function getProfilePathByRole(userRole?: string): string {
  const normalizedRole = (userRole || "").trim().toUpperCase();

  if (normalizedRole === "EMPL" || normalizedRole === "STAFF") {
    return zoneUrl("staff", "/profile");
  }

  if (normalizedRole === "WWWM" || normalizedRole === "MEMBER") {
    return zoneUrl("member", "/profile");
  }

  if (normalizedRole === "AGENCY" || normalizedRole === "COMPANY") {
    return "/booking/agencies/profile";
  }

  return zoneUrl("booking", "/profile");
}

function getLocalOriginByRole(userRole?: string): string | null {
  const normalizedRole = (userRole || "").trim().toUpperCase();

  if (normalizedRole === "EMPL" || normalizedRole === "STAFF") {
    return "http://localhost:3001";
  }

  if (normalizedRole === "WWWM" || normalizedRole === "MEMBER") {
    return "http://localhost:3004";
  }

  if (normalizedRole === "AGENCY" || normalizedRole === "COMPANY") {
    return "http://localhost:3003";
  }

  return null;
}

export default function Header({
  headerContent,
  selectedCountry,
  cultureNames,
  showModal = () => {},
  signupHref,
}: Readonly<HeaderProps>) {
  const [openControlled, setOpenControlled] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user } = useAuthState();
  const dispatch = useAppDispatch();
  const pathname = useZonePathname(); // locale- AND zone-stripped: compared against bare literals below
  const router = useLocalizedRouter();
  const hideLoginButton = ["/reset-password", "/login"].includes(pathname);
  const showLoginDialog = ["/", "/login", "/register"].includes(pathname);
  const showLoginModal =
    ["/flight", "/baggage", "/seats", "/extras", "/passengers", "/payment"].includes(pathname) ||
    pathname.endsWith("/itinerary");
  const handleLoginClick = () => {
    if (showLoginDialog) {
      setOpenControlled(true);
      return;
    }

    if (showLoginModal) {
      showModal();
      return;
    }

    router.push("/login");
  };
  const showDialog = (shouldOpen: boolean) => {
    setOpenControlled(shouldOpen);
  };

  const buildBookingLogoutUrl = (baseUrl?: string) => {
    // Every zone middleware now handles `/logout` itself, redirecting to MAIN with
    // ?action=logout so main's origin store gets swept. Without a resolved base URL the
    // path stays relative, so it must name THIS zone: in dev the zones are separate
    // origins and /booking/logout 404s on the staff or member one, leaving the user on a
    // dead page with main still holding an authenticated store.
    const logoutPath = zoneUrl(CURRENT_ZONE ?? "booking", "/logout");

    if (!baseUrl) {
      return logoutPath;
    }

    return `${baseUrl.replace(/\/$/, "")}${logoutPath}`;
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);

    try {
      console.log("Resolving base URL for logout redirect...");
      // 1. Resolve redirect URL first (while session is stable)
      let logoutRedirectUrl = buildBookingLogoutUrl();
      try {
        const { getBaseUrl } = await import("@repo/token/actions/getBaseUrl");

        const baseUrl = await getBaseUrl();
        console.log("Base URL for logout redirect...", baseUrl);
        logoutRedirectUrl = buildBookingLogoutUrl(baseUrl);
      } catch (err) {
        console.warn("Failed to resolve base URL, using fallback", err);
      }

      // 2. Best-effort server logout
      try {
        await getNewToken();
      } catch (e) {
        console.warn("Server logout failed", e);
      }

      // 3. Manual cleanup of storage
      // We avoid dispatching Redux actions here to prevent React re-renders which trigger
      // background RSC fetches and cause CORS errors/redirects during unload.
      if (typeof window !== "undefined") {
        // The persisted store is ALWAYS keyed per tab: `persist:redux:<tabSessionId>`
        // (see cleanupReduxForTab). Removing the bare "persist:redux" removed a key that
        // never exists, so the authenticated store survived logout — measured live: the
        // slice still held `user`/`isAuthenticated` with no auth cookie present. Remove
        // this tab's key, plus any left by earlier sessions in the same tab.
        const sessionId = getTabSessionId();
        if (sessionId) {
          cleanupReduxForTab(sessionId);
        }
        try {
          Object.keys(window.localStorage)
            .filter((k) => k.startsWith(`persist:${PERSIST_KEY}`))
            .forEach((k) => window.localStorage.removeItem(k));
        } catch {
          // storage unavailable (private mode / blocked) — the post-logout
          // ReduxInitializer sweep is the backstop
        }
        window.sessionStorage.clear();

        // Clear marker cookie
        document.cookie = "tab_session_persistence=; path=/; max-age=0; SameSite=Lax";
      }

      // 4. Navigate (anonymous entry)
      window.location.href = logoutRedirectUrl;
    } catch {
      // Fallback
      window.location.href = buildBookingLogoutUrl();
    }
  };

  const handleProfile = async () => {
    const profilePath = getProfilePathByRole(user?.userRole);

    const localOrigin =
      process.env.NODE_ENV === "production" ? null : getLocalOriginByRole(user?.userRole);

    window.location.assign(localOrigin ? `${localOrigin}${profilePath}` : profilePath);
  };

  const handleLogoClick = async () => {
    try {
      setIsLoggingOut(true);

      // Clear all Redux state except locale/currency
      dispatch(resetToMain());

      // Get base URL and navigate to home
      const { getBaseUrl } = await import("@repo/token/actions/getBaseUrl");
      const baseUrl = await getBaseUrl();
      window.location.href = `${baseUrl.replace(/\/$/, "")}${homeUrl()}`;
    } catch (error) {
      console.error("Error navigating to home:", error);
      setIsLoggingOut(false);
    }
  };

  const { isSessionReady } = useTokenRefresh();

  useEffect(() => {
    // Skip fetching user on landing/login pages only (no authenticated session)
    const isNoAuthPage = pathname === "/" || pathname === "/login";
    if (isNoAuthPage) {
      return;
    }

    const fetchUserData = async () => {
      // Wait for session to be ready before making API calls
      if (!isSessionReady) {
        return;
      }

      // Get tab session ID - this must be available before making API calls
      const tabSessionId = getTabSessionId();

      // Don't fetch if tab session ID is not set yet
      if (!tabSessionId) {
        return;
      }

      try {
        const data = await fetchUser(tabSessionId);
        if (roles.includes(data.userRole)) {
          const userData: AuthUser = {
            userRole: data.userRole,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            userKey: data.userKey,
            username: data.username ?? data.user?.data?.username,
            programs: data.userData.programs,
          };
          dispatch(setAuth(userData));
        }
      } catch (error) {
        console.error("[Header] Error fetching user:", error);
      }
    };

    // Fetch user data - token is managed via cookies
    fetchUserData();
  }, [dispatch, isSessionReady, pathname]);

  // Show loading overlay during logout
  if (isLoggingOut) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-white"
        data-testid="header-loading-section"
      >
        <FlightSpinLoader size="large" />
      </div>
    );
  }

  return (
    <header data-testid="header" className="fixed top-0 z-[300] w-full bg-white xl:h-16">
      <div
        className="hidden w-full p-2 xl:p-4 bg-white border-b-4 border-b-brand-red lg:flex"
        data-testid="header-desktop-section"
      >
        <div
          className="flex flex-row items-center justify-between w-full max-w-[1200px] m-auto gap-4 xl:gap-8"
          data-testid="header-desktop-container"
        >
          <Image
            src={JetSmartLogo}
            alt="Jetsmart"
            width={136}
            height={48}
            data-testid="desktop-header-jetsmart-logo"
            className="cursor-pointer"
            onClick={handleLogoClick}
          />
          <div
            className="flex flex-row items-center justify-between gap-2 xl:gap-4"
            data-testid="header-desktop-actions"
          >
            <DesktopMenuGate header={headerContent} />
            {!user && <RegisterButton signupHref={signupHref} />}

            {!hideLoginButton &&
              (user ? (
                <UserDropdown
                  headerContent={headerContent}
                  userName={user.firstName}
                  onLogout={handleLogout}
                  onProfile={handleProfile}
                />
              ) : (
                <JetSmartButton
                  bgColor="bg-brand-blue"
                  data-testid="desktop-header-login-btn"
                  hoverColor="hover\:bg-brand-red"
                  textColor="text-white"
                  borderColor=""
                  iconAlt="Round Arrow"
                  icon={<ChevronRightCircle />}
                  iconClassName="w-5 h-5"
                  className="capitalize"
                  onClick={handleLoginClick}
                >
                  {headerContent.loginButtonText}
                </JetSmartButton>
              ))}

            <CountrySelector
              countryDropdown={headerContent.countryDropdown}
              selectedCountry={selectedCountry}
              cultureNames={cultureNames}
            />
          </div>
        </div>
        {showLoginDialog && (
          <LoginDialog
            openControlled={openControlled}
            setOpenControlled={showDialog}
            homepageLoginModal={headerContent.homepageLoginModal}
          />
        )}
      </div>
      <div className="lg:hidden" data-testid="header-mobile-section">
        <MobileMenu
          menuItems={headerContent.navigationItems}
          countryDropdown={headerContent.countryDropdown}
          selectedCountry={selectedCountry}
          openControl={() => {
            if (user) return;
            handleLoginClick();
          }}
          loginButtonText={user ? user.firstName : headerContent.loginButtonText}
          hideLoginButton={hideLoginButton}
          user={user}
          onLogout={handleLogout}
          onProfile={handleProfile}
          onLogoClick={handleLogoClick}
          cultureNames={cultureNames}
          headerContent={headerContent}
        />
      </div>
    </header>
  );
}
